/**
 * PageScaffold (v4) — the shared responsive page frame for CAR-IA content
 * pages. Unified TopBar (with nav) + lime hero + a max-width content rail +
 * mobile BottomNav. Reused by the marketing / info pages and by the
 * example scaffolds for not-yet-built scopes, so every page inherits the
 * same chrome, spacing and responsive behaviour.
 */
import React from 'react';

import './caria-components.css';
import { BottomNav } from './BottomNav';
import { ColorRail, TopBar, TopBarNavItem, BUYER_NAV } from './TopBar';

export interface PageScaffoldProps {
  title: string;
  eyebrow?: string;
  lead?: React.ReactNode;
  /** Buttons rendered under the hero lead. */
  heroCta?: React.ReactNode;
  /** Hide the hero band entirely (page renders content straight under the bar). */
  hideHero?: boolean;
  /** Content rail width. */
  width?: 'wide' | 'narrow' | 'tight';
  nav?: TopBarNavItem[];
  /**
   * Identity for the TopBar. Defaults to UNSET, i.e. signed-out, which renders
   * the "Ingresar" action. This used to default to 'CA' / "Mi cuenta", so every
   * scaffold page showed anonymous visitors a fake signed-in account (DEF-004).
   * Callers pass `useAuthChrome()`; commodity/ does not import from core/.
   */
  avatarInitials?: string;
  accountName?: string;
  children?: React.ReactNode;
}

const widthClass = (w: PageScaffoldProps['width']) =>
  w === 'narrow' ? ' cia-container--narrow' : w === 'tight' ? ' cia-container--tight' : '';

export const PageScaffold: React.FC<PageScaffoldProps> = ({
  title,
  eyebrow,
  lead,
  heroCta,
  hideHero = false,
  width = 'wide',
  nav = BUYER_NAV,
  avatarInitials,
  accountName,
  children,
}) => (
  <div className="cia-page">
    <TopBar avatarInitials={avatarInitials} accountName={accountName} nav={nav} />
    <ColorRail />

    {!hideHero && (
      <header className="cia-hero">
        <div className="cia-container">
          {eyebrow && <p className="cia-hero__eyebrow">{eyebrow}</p>}
          <h1 className="cia-hero__title">{title}</h1>
          {lead && <p className="cia-hero__lead">{lead}</p>}
          {heroCta && <div className="cia-hero__cta">{heroCta}</div>}
        </div>
      </header>
    )}

    <main
      className={`cia-container${widthClass(width)}`}
      style={{ paddingTop: 32, paddingBottom: 8 }}
    >
      {children}
    </main>

    <BottomNav />
  </div>
);

export interface ComingSoonProps {
  /** What this page will do; shown as the block title. */
  title: string;
  lead: React.ReactNode;
  /** Bullet list of planned capabilities (from the prototype). */
  features?: string[];
  /** The prototype file this scaffold is based on. */
  prototype?: string;
}

/**
 * ComingSoon — the placeholder content block for example pages that are
 * scoped for a future increment. Renders the planned scope so stakeholders
 * can see the intent without the page being functional yet.
 */
export const ComingSoon: React.FC<ComingSoonProps> = ({ title, lead, features, prototype }) => (
  <section className="cia-soon">
    <span className="cia-soon__badge">Próximo scope</span>
    <h2 className="cia-soon__title">{title}</h2>
    <p className="cia-soon__lead">{lead}</p>
    {features && features.length > 0 && (
      <ul className="cia-soon__list">
        {features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
    )}
    {prototype && (
      <p style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-5)' }}>
        Prototipo de referencia: <code>{prototype}</code>
      </p>
    )}
  </section>
);

export default PageScaffold;
